import { useQuery } from '@tanstack/react-query';
import { axiosGet } from '../api/axios';

export interface SystemMessage {
  archived_at: any;
  audience?: 'member' | 'peer' | 'admin' | 'shared';
  client_blacklist: any;
  delay_while_on_call: boolean;
  description: string;
  email_template: any;
  group: Group;
  group_id: number;
  id: number;
  key: string;
  message: string;
  name: string;
  no_group_logo: boolean;
  send_app_message: boolean;
  send_email: boolean;
  send_notification: boolean;
  send_sms: boolean;
  send_sms_as_primary: boolean;
  sms_channel_id: any;
  sms_data: any;
  sms_media: any;
  subject: string;
}

export interface Group {
  description: string;
  id: number;
  key: string;
  name: string;
}

/**
 * System messages offered when rejecting listener experiences (`listener_message_rejected` only).
 * De-duplicates by id, then by display name — comms data often has multiple rows with the same title
 * (different ids). Keeps the lowest id when names collide.
 */
export function listenerRejectTemplateMessages(systemMessages: SystemMessage[] | undefined): SystemMessage[] {
  if (!systemMessages?.length) return [];
  const filtered = systemMessages.filter((m) => m.group?.key === 'listener_message_rejected');
  const byId = new Map<number, SystemMessage>();
  for (const m of filtered) {
    byId.set(m.id, m);
  }
  const uniqueById = [...byId.values()];
  const byDisplayName = new Map<string, SystemMessage>();
  for (const m of uniqueById) {
    const labelKey = m.name.trim().toLowerCase();
    const existing = byDisplayName.get(labelKey);
    if (!existing || m.id < existing.id) {
      byDisplayName.set(labelKey, m);
    }
  }
  return [...byDisplayName.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

const getSystemMessages = async (params: Record<string, string> = {}): Promise<SystemMessage[]> => {
  return await axiosGet('/comms/system_messages', params, 'v2').then((commsResponse) => commsResponse.data);
};

export const useSystemMessages = (params: Record<string, string> = {}) => {
  const { data, isLoading, error } = useQuery<SystemMessage[]>(['system_messages', params], () =>
    getSystemMessages(params),
  );

  return { data, isLoading, error };
};

export type EmailTemplateMetadataResponse = EmailTemplateMetadata[];

export interface EmailTemplateMetadata {
  id: string;
  labels: string[];
  name: string;
}

const getEmailTemplateMetadata = async (): Promise<EmailTemplateMetadataResponse> => {
  return await axiosGet('/comms/email_template_metadata', {}, 'v2').then(
    (emailTemplateMetadataResponse) => emailTemplateMetadataResponse.data,
  );
};

export const useEmailTemplateMetadata = () => {
  return useQuery<EmailTemplateMetadataResponse>(['email_template_metadata'], () => getEmailTemplateMetadata());
};

export type MergeVarCategory = 'user' | 'client' | 'domain' | 'powered_by' | 'email' | 'conditional';

export interface MergeVarCategoryDoc {
  id: MergeVarCategory;
  label: string;
}

export interface MergeVarDoc {
  key: string;
  category: MergeVarCategory;
  description: string;
  editor_visible: boolean;
  caveat?: string;
}

export interface MergeVarsResponse {
  categories: MergeVarCategoryDoc[];
  variables: MergeVarDoc[];
}

const getMergeVars = async (): Promise<MergeVarsResponse> => {
  return await axiosGet('/comms/merge_vars', {}, 'v3').then((response) => response.data);
};

export const useMergeVars = (enabled = true) => {
  return useQuery<MergeVarsResponse>(['comms_merge_vars'], () => getMergeVars(), { enabled });
};
